import React from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import type { QuestionResult } from '../types';
import { cn } from '../utils/cn';

interface QuizResultProps {
  score: number;
  results: QuestionResult[];
  onRetry?: () => void;
  onContinue: () => void;
}

export function QuizResult({ score, results, onRetry, onContinue }: QuizResultProps) {
  const passed = score >= 70;

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className={cn(
        "text-center p-8 rounded-xl",
        passed ? "bg-green-50" : "bg-red-50"
      )}>
        <div className={cn(
          "mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4",
          passed ? "bg-green-100" : "bg-red-100"
        )}>
          {passed ? (
            <CheckCircle className="h-8 w-8 text-green-600" />
          ) : (
            <XCircle className="h-8 w-8 text-red-600" />
          )}
        </div>

        <h2 className="text-2xl font-bold mb-2">
          {passed ? "Parabéns!" : "Não foi dessa vez..."}
        </h2>
        
        <p className="text-lg">
          Você atingiu <span className="font-bold">{score.toFixed(1)}%</span> de aproveitamento
        </p>
      </div>

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Revisão das Questões</h3>
        
        {results.map((result, index) => (
          <div 
            key={`result-${result.questionId}`}
            className={cn(
              "p-6 rounded-lg border-2",
              result.isCorrect ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-gray-900">
                  {index + 1}. {result.question}
                </p>
                
                <div className="mt-2 space-y-1">
                  <p className="text-sm text-gray-700">Sua resposta:</p>
                  <p className={cn(
                    "text-sm pl-4",
                    result.isCorrect ? "text-green-700" : "text-red-700"
                  )}>
                    {result.userAnswer}
                  </p>
                </div>

                {!result.isCorrect && (
                  <div className="mt-4 pt-4 border-t border-red-200">
                    <p className="text-sm font-medium text-gray-900">
                      Resposta correta:
                    </p>
                    <p className="text-sm text-green-700 pl-4">
                      {result.correctAnswer}
                    </p>
                  </div>
                )}
              </div>

              {result.isCorrect ? (
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end gap-4 pt-4">
        {!passed && onRetry && (
          <button
            onClick={onRetry}
            className={cn(
              "px-6 py-2 rounded-lg border-2 border-red-200 text-red-700",
              "transition-colors hover:bg-red-50"
            )}
          >
            Tentar Novamente
          </button>
        )}

        <button
          onClick={onContinue}
          className={cn(
            "px-6 py-2 rounded-lg text-white",
            "transition-all hover:opacity-90 active:scale-95",
            "bg-[#7c3aed]"
          )}
        >
          {passed ? "Continuar" : "Voltar ao Módulo"}
        </button>
      </div>
    </div>
  );
}